import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@mui/material';
import { useUpdateEffect } from 'react-use';
import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { EventForm } from '@components/eventCreation/CreateEventUtils';
import { ClassToInsert, Division, DivisionToInsert } from '@type/db-types';
import useStateWithHistory from '@hooks/useStateWithHistory'; // use custom hook to fix bugs: https://github.com/streamich/react-use/issues/2061#issuecomment-1794705932
import { isStringNumeric } from '@utils/shared-utils';

const someMaxWeightIsNumeric = (divisions: DivisionToInsert[] | Division[]): boolean =>
    divisions.some((division) =>
        division.classes.some(
            (weightClass) =>
                Number(weightClass.maxWeight) !== 0 && isStringNumeric(weightClass.maxWeight),
        ),
    );

// ask to auto adjust weight tolerance when changing it
export default function useWatchWeightTolerance() {
    const [open, setOpen] = useState(false);
    const { setValue, watch } = useFormContext<EventForm>();
    const [divisions, weightTolerance] = watch(['divisions', 'weightTolerance']);

    const [weightToleranceLast, setWeightToleranceLast, weightToleranceLastHistory] =
        useStateWithHistory(weightTolerance, 2, [weightTolerance]);

    // Prompt user when weightTolerance is changed, ask if they want to adjust all weight classes now or no thanks
    // if yes, adjust all weight classes by the new weightTolerance
    // if no, don't adjust any weight classes
    useUpdateEffect(() => {
        if (
            typeof weightTolerance === 'number' &&
            weightTolerance !== weightToleranceLast &&
            weightToleranceLastHistory.history[weightToleranceLastHistory.history.length - 1] !==
                weightTolerance &&
            someMaxWeightIsNumeric(divisions)
        ) {
            setWeightToleranceLast(weightTolerance);
            setOpen(true);
        }
    }, [weightTolerance]);

    const cancelAdjustment = useCallback(() => {
        setOpen(false);
    }, []);

    const startAdjustment = useCallback(() => {
        setOpen(false);
        setValue(
            'divisions',
            divisions.map((division) => {
                const newDivision = { ...division };
                newDivision.classes = newDivision.classes.map((weightClass) => {
                    // Example: old tolerance: 10, new tolerance: 11, diff: new - old = 1
                    const currentTolerance =
                        weightToleranceLastHistory.history[
                            weightToleranceLastHistory.history.length - 1
                        ] || 0;
                    const lastEntry =
                        weightToleranceLastHistory.history[
                            weightToleranceLastHistory.history.length - 2
                        ] || 0;

                    const numMaxWeight = Number(weightClass.maxWeight);

                    const newWeightClass: ClassToInsert = {
                        ...weightClass,
                        maxWeight: isStringNumeric(weightClass.maxWeight)
                            ? // if maxWeight is a number, add the difference to the maxWeight
                              `${(numMaxWeight || 0) + (currentTolerance - (lastEntry === currentTolerance ? 0 : lastEntry))}`
                            : // if maxWeight is not a number, keep it as is
                              weightClass.maxWeight,
                    };
                    return newWeightClass;
                });
                return newDivision;
            }),
        );
    }, [divisions, setValue, weightToleranceLastHistory.history]);

    return open ? (
        <Dialog open={open} aria-labelledby="dialog-title" aria-describedby="dialog-description">
            <DialogTitle id="dialog-title">{'Update all weight classes?'}</DialogTitle>
            <DialogContent>
                <DialogContentText id="dialog-description">
                    We'll update any weight classes that have been created to reflect the new weight
                    tolerance. Max weight will go up or down based on the new tolerance.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={cancelAdjustment}>Keep same weights as before</Button>
                <Button onClick={startAdjustment} autoFocus variant="contained">
                    Adjust weight classes accordingly
                </Button>
            </DialogActions>
        </Dialog>
    ) : null;
}
